import { useNotify, UserPermission } from '@globaleris/ui.library'
import { Button, Grid, makeStyles, useTheme } from '@material-ui/core'
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useHistory } from 'react-router'
import {
  EnumEstadoCiclo,
  EnumTipoCicloFacturacion,
  EnumTipoFacturacion,
  EnumTipoSujeto
} from '../../api'
import { SettlementBatchRequestModel } from '../../api/Argentia/endpoints/settlementBatchs/model/settlementBatchs'
import {
  SettlementsKPIsResponseModel,
  SettlementsObjectionsTableModel,
  SettlementsStatusKPIsResponseModel,
  SettlementTableModel
} from '../../api/Argentia/endpoints/settlements/model/settlements'
import { EnumDominioTarea } from '../../api/oData/enums/enumDominioTarea'
import { EnumEstadoLiquidacion } from '../../api/oData/enums/enumEstadoLiquidacion'
import { EnumTipoLoteLiquidacion } from '../../api/oData/enums/enumTipoLoteLiquidacion'
import Page, { PageLayout } from '../../components/page/page'
import PageCenterColumn from '../../components/page/pageCenterColumn'
import PageHeader from '../../components/page/pageHeader'
import { CardActionProps } from '../../components/shared/cardActions/cardActions'
import { ObjectionsDialog } from '../../components/shared/objectionsDialog/objectionsDialog'
import { ButtonOption } from '../../components/shared/splitButton/splitButton'
import { envConfig } from '../../config/envConfig'
import { getUserPermissions, hasPermission } from '../../helpers/permissionsHelper'
import { useConfirmationDialog } from '../../hooks'
import { mapObjectionTableModelToObjectionModel } from '../../mappers/objections.mapper'
import { NonAuthRoutes } from '../../model'
import { ConfirmationDialogs } from '../../model/enums/confirmationDialogs'
import { InformationDialogs } from '../../model/enums/informationDialogs'
import { Pages, Resources } from '../../model/RBAC/constants'
import { createObjection, terminateObjection } from '../../services/objections.services'
import { useExecutionStatusNotification } from '../invoiceCycles/hooks/useExecutionStatusNotification'
import { CycleExecutionModel } from '../invoiceCycles/invoiceCycles.model'
import {
  fetchGlobalInvoiceCycleObjections,
  fetchInvoiceCycleExecution
} from '../invoiceCycles/invoiceCycles.service'
import { ObjectionTableModel } from '../invoicesPage/invoicesPage.Model'
import { CloseExecutionCycleDialog } from './components/closeExecutionCycleDialog'
import { GlobalObjectionsCard } from './components/globalObjectionsCard/globalObjectionsCard'
import { RegenerateExecutionCycleDialog } from './components/regenerateExecutionCycleDialog/regenerateExecutionCycleDialog'
import { SearchFilterCard } from './components/searchFilterCard/searchFilterCard'
import { SettlementKpiBlocks } from './components/settlementKpiBlocks/settlementKpiBlocks'
import { SettlementKpiBlocksCard } from './components/settlementKpiBlocksCard/settlementKpiBlocksCard'
import { SettlementKpiType } from './components/settlementKpiRightContent/settlementKpiRightContent'
import { SettlementObjectionsCard } from './components/settlementsObjectionsCard/settlementsObjectionsCard'
import { SettlementsTableCard } from './components/settlementsTableCard/settlementsTableCard'
import { TerminateObjectionsDialog } from './components/terminateObjectionsDialog/terminateObjectionsDialog'
import {
  useCloseExecutionCycleDialog,
  useConfirmSettlementsBatchLaunch,
  useExecutionCycleActions,
  useNavigateToSettlementsInvoices,
  useSettlementBatchLaunch
} from './hooks'
import {
  completeDataTableActions,
  mapFiltersToSearchRequest,
  mapGlobalObjectionsResponseToTableModel,
  mapObjectionsResponseToTableModel,
  mapSelectedRowsToSettlementBatchRequestModel
} from './settlements.mapper'
import {
  EnumSettlementBatchMode,
  EnumSettlementSearchType,
  EnumSettlementTableCustomActionConfig,
  GlobalObjectionTableModel,
  SettlementFormSearchFilter
} from './settlements.models'
import {
  fetchObjectionsBySettlementID,
  getInvoiceCycleByID,
  getSettlementLandingKpis,
  getSettlementObjections,
  getSettlementsStatusKpis,
  getSettlementsTableData
} from './settlements.service'
import { styles } from './settlements.styles'

interface SettlementsPageProps {
  location?: {
    state: {
      executionCycleId?: number
      invoiceCycleId?: number
      /** Estado desde el que se llegó tras lanzar Regenerar (la ejecución puede no haber arrancado) */
      waitForProcessingFrom?: EnumEstadoCiclo
    }
  }
}

const INITIAL_FILTERS: SettlementFormSearchFilter = {
  searchType: EnumSettlementSearchType.Contract,
  searchValue: ''
}

export const SettlementsPage: React.FC<SettlementsPageProps> = props => {
  const [t] = useTranslation(['globalerisLibrary', 'enums', 'settlements'])
  const { getInformation, getConfirmation, closeConfirmation } = useConfirmationDialog()
  const history = useHistory()
  const { info, error } = useNotify()
  const currentTheme = useTheme()
  const useStyles = makeStyles(styles(currentTheme))
  const classes = useStyles()

  const [isFetchingKpis, setIsFetchingKpis] = useState<boolean>(false)
  const [isFetchingStatusKpis, setIsFetchingStatusKpis] = useState<boolean>(false)
  const [statusKpis, setStatusKpis] = useState<SettlementsStatusKPIsResponseModel | undefined>(
    undefined
  )
  const [isFetchingObjections, setIsFetchingObjections] = useState<boolean>(false)
  const [isFetchingSettlementsData, setIsFetchingSettlementsData] = useState<boolean>(false)
  const [isFetchingInvoiceCycle, setIsFetchingInvoiceCycle] = useState<boolean>(true)

  const [landingKpis, setLandingKpis] = useState<SettlementsKPIsResponseModel | undefined>(
    undefined
  )
  const [settlementObjections, setSettlementObjections] = useState<
    SettlementsObjectionsTableModel[]
  >([])
  const [settlementBatchObjections, setSettlementBatchObjections] = useState<
    GlobalObjectionTableModel[]
  >([])
  const [settlementsTableData, setSettlementsTableData] = useState<SettlementTableModel[]>([])

  const [openSettlementsObjections, setOpenSettlementsObjections] = useState<boolean>(false)
  const [openSettlementBatchObjections, setOpenSettlementBatchObjections] = useState<boolean>(false)
  const [openSettlementsTable, setOpenSettlementsTable] = useState<boolean>(false)

  const executionCycleId = props.location?.state?.executionCycleId ?? 0
  const invoiceCycleId = props.location?.state?.invoiceCycleId ?? 0

  const [invoiceCycleType, setInvoiceCycleType] = useState<EnumTipoCicloFacturacion | undefined>(
    undefined
  )
  const [subjectType, setSubjectType] = useState<EnumTipoSujeto | undefined>(undefined)
  const [billingType, setBillingType] = useState<EnumTipoFacturacion | undefined>(undefined)
  const [invoiceCycleDescription, setInvoiceCycleDescription] = useState<string | undefined>(
    undefined
  )

  const [customSelectedRowsMode, setCustomSelectedRowsMode] = useState<boolean>(false)
  const [viewObjectionsMode, setViewObjectionsMode] = useState<boolean>(false)

  // External row selection control
  const [selectedSettlements, setSelectedSettlements] = useState<number[]>([])
  const [selectedSettlementsData, setSelectedSettlementsData] = useState<SettlementTableModel[]>([])

  const [showObjectionsDialog, setShowObjectionsDialog] = useState<boolean>(false)
  const [showTerminateObjectionsDialog, setShowTerminateObjectionsDialog] = useState<boolean>(false)
  const [terminateObjectionsRows, setTerminateObjectionsRows] = useState<SettlementTableModel[]>([])
  const [settlementRowObjections, setSettlementRowObjections] = useState<
    ObjectionTableModel[] | undefined
  >(undefined)
  const [isLoadingObjections, setIsLoadingObjections] = useState<boolean>(false)
  const [settlementRow, setSettlementRow] = useState<SettlementTableModel | undefined>(undefined)
  const [selectedObjection, setSelectedObjection] = useState<
    SettlementsObjectionsTableModel | undefined
  >(undefined)
  const selectedObjectionRef = React.useRef(selectedObjection)
  selectedObjectionRef.current = selectedObjection

  const [showSearchFilter, setShowSearchFilter] = useState<boolean>(false)
  const [searchFilters, setSearchFilters] = useState<SettlementFormSearchFilter>(INITIAL_FILTERS)
  const [isSearchMode, setIsSearchMode] = useState<boolean>(false)

  const [isFinishedExecutionCyclePage, setIsFinishedExecutionCyclePage] = useState<boolean>(false)
  const [executionData, setExecutionData] = useState<CycleExecutionModel | null>(null)
  const [isFetchingExecution, setIsFetchingExecution] = useState<boolean>(false)
  const cycleStatus: EnumEstadoCiclo | null = (executionData?.status as EnumEstadoCiclo) ?? null
  // Cerrada: informada por el KPI o porque la ejecución pasó a Finalizado (solo lectura)
  const isExecutionClosed =
    isFinishedExecutionCyclePage || cycleStatus === EnumEstadoCiclo.Finalizado

  // KPI card selection state
  const [activeKpi, setActiveKpi] = useState<SettlementKpiType | null>(null)
  const [settlementsTableKey, setSettlementsTableKey] = useState(0)
  const resultsRef = useRef<HTMLDivElement>(null)

  // Al activar un KPI se baja suavemente hasta los resultados (tabla o tarjeta que se abre)
  useEffect(() => {
    if (!activeKpi) return
    const timeout = window.setTimeout(() => {
      const prefersReducedMotion = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches
      resultsRef.current?.scrollIntoView({
        behavior: prefersReducedMotion ? 'auto' : 'smooth',
        block: 'start'
      })
    }, 150)
    return () => window.clearTimeout(timeout)
  }, [activeKpi])

  // Base search configuration - reutilizable
  // Type assertion segura: siempre se valida antes de usar
  const baseSearchConfig = useMemo(
    () => ({
      executionCycleId: executionCycleId,
      invoiceCycleType: invoiceCycleType!,
      subjectType: subjectType!,
      billingType: billingType!,
      cycleState: EnumEstadoCiclo.PdteValidarListado
    }),
    [executionCycleId, invoiceCycleType, subjectType, billingType]
  )

  const openSettlements = useMemo(() => {
    return settlementsTableData.filter(
      settlement =>
        settlement.state === EnumEstadoLiquidacion.Abierta ||
        settlement.state === EnumEstadoLiquidacion.Pendiente
    )
  }, [settlementsTableData])

  // #region Handlers Starts

  // #region ------------------- Header Action Buttons Handlers -----------------------------//

  const {
    handleRegenerateExecutionCycle,
    closeExecutionCycle,
    ensureExecutionCanBeModified,
    regenerateDialogOpen,
    handleRegenerateDialogClose,
    regenerateInitialData
  } = useExecutionCycleActions({
    executionCycleId,
    invoiceCycleId: invoiceCycleId!,
    invoiceCycleDescription,
    invoiceCycleType,
    subjectType,
    billingType,
    currentCycleState: EnumEstadoCiclo.PdteValidarListado,
    executionInitialData: executionData
      ? {
          startDate: executionData.startDate,
          endDate: executionData.endDate,
          invoiceCycleSubtype: executionData.subtype
        }
      : undefined,
    // Regenerar: la pantalla se queda con el faldón esperando que la ejecución arranque
    onRegenerateResult: isSuccess =>
      isSuccess
        ? startWaitingForProcessing(EnumEstadoCiclo.PdteValidarListado)
        : handleReloadExecution(),
    // Cerrar/Regenerar bloqueado porque otra ventana cambió el estado: se recarga para mostrarlo
    onExecutionNotModifiable: () => handleReloadExecution()
  })

  const { isOpen, openDialog, closeDialog, onCloseWithInvoices, onCloseWithoutInvoices } =
    useCloseExecutionCycleDialog({
      closeExecutionCycle,
      canOpen: ensureExecutionCanBeModified
    })

  const { navigateToSettlementsInvoices } = useNavigateToSettlementsInvoices()

  const { confirmSettlementsBatchLaunch, confirmMassiveBatchLaunch } =
    useConfirmSettlementsBatchLaunch(executionCycleId)

  // Tras crear el lote se pregunta si ir a Procesando o seguir revisando (con el faldón a la espera)
  const { launchSettlementBatch } = useSettlementBatchLaunch({
    executionCycleId,
    invoiceCycleId,
    cycleName: invoiceCycleDescription,
    originState: EnumEstadoCiclo.PdteValidarListado,
    onExecutionClosed: () => handleReloadExecution(),
    onKeepReviewing: () => {
      // Se refresca la info y se limpia la selección: las liquidaciones lanzadas ya no deben verse como pendientes
      setSelectedSettlements([])
      setSelectedSettlementsData([])
      setSettlementsTableKey(k => k + 1)
      handleReloadExecution()
      startWaitingForProcessing(EnumEstadoCiclo.PdteValidarListado)
    }
  })

  const handleViewSettlementsInvoices = () => {
    navigateToSettlementsInvoices({
      executionCycleId,
      invoiceCycleId,
      invoiceCycleType,
      subjectType,
      billingType,
      isFinishedExecutionCycle: isFinishedExecutionCyclePage
    })
  }

  // #endregion ------------------- Header Action Buttons Handlers -----------------------------//

  // #region ------------------- Footer Action Buttons Handlers -----------------------------//

  const handleViewSearchCard = () => {
    setShowSearchFilter(!showSearchFilter)
    if (!showSearchFilter) {
      setCustomSelectedRowsMode(true)
      setOpenSettlementsObjections(false)
      setViewObjectionsMode(false)
      setOpenSettlementBatchObjections(false)
    }
  }

  const handleSearchFilterSubmit = (formState: SettlementFormSearchFilter) => {
    setSearchFilters(formState)
    setIsSearchMode(true)

    const searchRequest = mapFiltersToSearchRequest(formState, baseSearchConfig)

    setIsFetchingSettlementsData(true)
    setOpenSettlementsTable(true)

    getSettlementsTableData(searchRequest)
      .then(res => {
        if (res.success && res.data) {
          if (res.data.exceedsLimit && res.data.settlements.length === 0) {
            setSettlementsTableData([])
            error(
              t('settlements:notifications.fetchFilteredSettlements.exceedsLimitRefine', {
                total: res.data.totalCount
              })
            )
          } else if (res.data.totalCount > 0) {
            setSettlementsTableData(completeDataTableActions(res.data.settlements))

            if (res.data.exceedsLimit) {
              info(
                t('settlements:notifications.fetchFilteredSettlements.exceedsLimit', {
                  shown: res.data.maxResults,
                  total: res.data.totalCount
                })
              )
            }
          } else {
            setSettlementsTableData([])
            error(t('settlements:notifications.fetchFilteredSettlements.noResults'))
          }
        }
      })
      .finally(() => setIsFetchingSettlementsData(false))
  }

  const handleCancelSearch = () => {
    setShowSearchFilter(false)
    setIsSearchMode(false)
  }

  /**
   * Valida si el valor del KPI excede el límite de consulta
   * @param kpiValue - Valor del KPI a validar
   * @returns true si puede proceder, false si excede el límite
   */
  const validateKpiQueryLimit = useCallback(
    async (kpiValue: number): Promise<boolean> => {
      if (kpiValue > envConfig.invoiceCycleConfig.maxQueryItems) {
        await getInformation(InformationDialogs.LIMIT_QUERY_RESULTS_SEARCHFILTER).then(() => {
          closeConfirmation()
        })
        return false
      }
      return true
    },
    [getInformation, closeConfirmation]
  )

  const handleRestartModesButtonClick = useCallback(() => {
    setActiveKpi(null)
    setCustomSelectedRowsMode(false)
    setViewObjectionsMode(false)
    setOpenSettlementsObjections(false)
    setOpenSettlementBatchObjections(false)
    setOpenSettlementsTable(false)
    setSettlementsTableData([])
  }, [])

  const fetchSettlementsObjections = useCallback(async () => {
    setIsFetchingObjections(true)
    await getSettlementObjections(executionCycleId)
      .then(res => {
        if (res?.success && res?.data && res.data.length > 0) {
          setSettlementObjections(res.data)
        }
      })
      .finally(() => setIsFetchingObjections(false))
  }, [executionCycleId])

  const fetchSettlementsTableData = useCallback(
    async (objCode: string | null, hasObjections?: boolean) => {
      if (!invoiceCycleType || !subjectType || !billingType) {
        return
      }

      setIsFetchingSettlementsData(true)

      const request = mapFiltersToSearchRequest(
        {
          searchType: EnumSettlementSearchType.Contract,
          searchValue: '',
          ...(hasObjections !== undefined && { hasObjections })
        },
        {
          ...baseSearchConfig,
          objCode: objCode
        }
      )

      await getSettlementsTableData(request)
        .then(res => {
          if (res?.success && res?.data) {
            setSettlementsTableData(completeDataTableActions(res.data.settlements))
          }
        })
        .finally(() => setIsFetchingSettlementsData(false))
    },
    [invoiceCycleType, subjectType, billingType, baseSearchConfig]
  )

  // #region ------------------- KPI Click Handlers -----------------------------//
  const handleKpiClick = useCallback(
    async (kpiType: SettlementKpiType) => {
      // Si se hace click en el mismo KPI activo, lo deseleccionamos
      if (activeKpi === kpiType) {
        setActiveKpi(null)
        handleRestartModesButtonClick()
        return
      }

      if (!landingKpis || !invoiceCycleType || !subjectType || !billingType) {
        return
      }

      setActiveKpi(kpiType)
      setSelectedSettlements([])
      setSelectedSettlementsData([])
      setSettlementsTableKey(k => k + 1)

      switch (kpiType) {
        case 'all':
          // Ver todas las liquidaciones
          if (!(await validateKpiQueryLimit(landingKpis.totalSettlements ?? 0))) {
            setActiveKpi(null)
            return
          }
          setCustomSelectedRowsMode(false)
          setViewObjectionsMode(false)
          setShowSearchFilter(false)
          setOpenSettlementsObjections(false)
          setOpenSettlementBatchObjections(false)
          setOpenSettlementsTable(true)
          fetchSettlementsTableData(null)
          break
        case 'validated':
          // Ver todas las liquidaciones validadas
          if (!(await validateKpiQueryLimit(landingKpis.totalSettlementsValidated ?? 0))) {
            setActiveKpi(null)
            return
          }
          setCustomSelectedRowsMode(true)
          setViewObjectionsMode(false)
          setShowSearchFilter(false)
          setOpenSettlementsObjections(false)
          setOpenSettlementBatchObjections(false)
          setOpenSettlementsTable(true)
          setIsFetchingSettlementsData(true)
          const request = mapFiltersToSearchRequest(
            {
              searchType: EnumSettlementSearchType.Contract,
              searchValue: '',
              hasObjections: false
            },
            {
              ...baseSearchConfig,
              objCode: null
            }
          )
          await getSettlementsTableData(request)
            .then(res => {
              if (res?.success && res?.data && res.data.settlements.length > 0) {
                setSettlementsTableData(completeDataTableActions(res.data.settlements))
              }
            })
            .finally(() => setIsFetchingSettlementsData(false))
          break
        case 'withObjections':
          // Ver liquidaciones con objeciones
          if (!(await validateKpiQueryLimit(landingKpis.totalSettlementsWithObjections ?? 0))) {
            setActiveKpi(null)
            return
          }
          // setting page flow
          setCustomSelectedRowsMode(false)
          setViewObjectionsMode(true)
          setShowSearchFilter(false)
          // cleaning previous state
          setOpenSettlementsTable(false)
          setSettlementsTableData([])
          setOpenSettlementBatchObjections(false)
          // show objections table
          setOpenSettlementsObjections(true)
          fetchSettlementsObjections()
          break
      }
    },
    [
      activeKpi,
      landingKpis,
      invoiceCycleType,
      subjectType,
      billingType,
      baseSearchConfig,
      validateKpiQueryLimit,
      handleRestartModesButtonClick,
      fetchSettlementsTableData,
      getSettlementsTableData,
      completeDataTableActions,
      fetchSettlementsObjections
    ]
  )
  // #endregion ------------------- KPI Click Handlers -----------------------------//

  const handleSettlementsSelectionChange = useCallback(
    (selectedRows: SettlementTableModel[], selectedIndices: number[]) => {
      const selectedIds = selectedRows.map(row => row.id).filter(id => id !== undefined)
      setSelectedSettlements(selectedIds)
      setSelectedSettlementsData(selectedRows)
    },
    []
  )

  const handleCustomSelectionClick = async (selectedRows: SettlementTableModel[]) => {
    await confirmSettlementsBatchLaunch(selectedRows, EnumEstadoLiquidacion.Pendiente).then(
      async confirmed => {
        if (!confirmed) return
        const request: SettlementBatchRequestModel = mapSelectedRowsToSettlementBatchRequestModel(
          selectedRows,
          [],
          EnumTipoLoteLiquidacion.Facturacion,
          executionCycleId,
          EnumSettlementBatchMode.INVOICE_CUSTOM_SELECTION
        )
        closeConfirmation()
        await launchSettlementBatch(request, { invoiceCycleType, subjectType, billingType })
      }
    )
  }

  const handleTerminateObjectionsClick = (selectedRows: SettlementTableModel[]) => {
    setTerminateObjectionsRows(selectedRows)
    setShowTerminateObjectionsDialog(true)
  }

  const handleViewObjectionsHistory = useCallback(() => {
    if (openSettlementBatchObjections) {
      setOpenSettlementBatchObjections(false)
    } else {
      setActiveKpi(null)
      setCustomSelectedRowsMode(false)
      setViewObjectionsMode(false)
      setShowSearchFilter(false)
      setOpenSettlementsTable(false)
      setSettlementsTableData([])
      setOpenSettlementsObjections(false)
      setOpenSettlementBatchObjections(true)
      fetchSettlementBatchsObjections()
    }
  }, [openSettlementBatchObjections])

  const refreshTableByContext = useCallback(() => {
    fetchSettlementAnnouncements()
    if (isSearchMode) {
      searchFilters && handleSearchFilterSubmit(searchFilters)
    } else if (openSettlementsTable) {
      // El KPI "facturables" consulta solo liquidaciones sin objeciones: se conserva al refrescar
      fetchSettlementsTableData(
        selectedObjectionRef.current?.objCode ?? null,
        activeKpi === 'validated' ? false : undefined
      )
    }
    if (openSettlementsObjections) fetchSettlementsObjections()
    if (openSettlementBatchObjections) fetchSettlementBatchsObjections()
  }, [
    activeKpi,
    isSearchMode,
    openSettlementsTable,
    openSettlementsObjections,
    openSettlementBatchObjections,
    searchFilters,
    handleSearchFilterSubmit
  ])

  const refreshCurrentSettlementObjections = useCallback(async () => {
    if (!settlementRow) return
    setIsLoadingObjections(true)
    const response = await fetchObjectionsBySettlementID(settlementRow.id).then(res => {
      if (res.success && res.data) {
        return res.data
      } else {
        error(t('settlementsInvoices:notifications.fetchObjections.error'))
        return []
      }
    })
    setSettlementRowObjections(mapObjectionsResponseToTableModel(response))
    setIsLoadingObjections(false)
  }, [settlementRow, error, t])

  const handleRefreshSettlementsAfterTerminate = useCallback(() => {
    setTerminateObjectionsRows([])
    setSelectedSettlements([])
    setSelectedSettlementsData([])
    setSettlementsTableKey(k => k + 1)
    refreshTableByContext()
  }, [refreshTableByContext])

  const handleRefreshObjectionsDialog = useCallback(() => {
    refreshTableByContext()
    refreshCurrentSettlementObjections()
  }, [refreshTableByContext, refreshCurrentSettlementObjections])

  const handleInvoiceValidated = () => {}

  const handleInvoicesSplitButtonClick = async (option: ButtonOption) => {
    let okToProceed = false
    let settlementBatchType: EnumTipoLoteLiquidacion = EnumTipoLoteLiquidacion.FacturacionAutomatica

    switch (option.id) {
      case EnumSettlementBatchMode.INVOICE_CUSTOM_SELECTION:
        // Usar las filas seleccionadas externamente
        if (selectedSettlementsData.length > 0) {
          await handleCustomSelectionClick(selectedSettlementsData)
        }
        return
      case EnumSettlementBatchMode.INVOICE_ALL:
        await confirmMassiveBatchLaunch().then(res => {
          okToProceed = true
          settlementBatchType = EnumTipoLoteLiquidacion.FacturacionAutomatica
          closeConfirmation()
        })
        break
      case EnumSettlementBatchMode.INVOICE_WITHOUT_OBJECTIONS:
        await confirmMassiveBatchLaunch().then(res => {
          okToProceed = true
          settlementBatchType = EnumTipoLoteLiquidacion.FacturacionSinObjeciones
          closeConfirmation()
        })
        break
      default:
        break
    }

    if (okToProceed) {
      const request: SettlementBatchRequestModel = mapSelectedRowsToSettlementBatchRequestModel(
        [],
        [],
        settlementBatchType,
        executionCycleId,
        option.id as EnumSettlementBatchMode
      )
      await launchSettlementBatch(request, { invoiceCycleType, subjectType, billingType })
    }
  }
  // #region ------------------- Objections Handlers -----------------------------//
  const handleViewSettlementObjections = async (selectedRow: SettlementTableModel) => {
    setShowObjectionsDialog(true)
    setSettlementRow(selectedRow)
    setIsLoadingObjections(true)
    const response = await fetchObjectionsBySettlementID(selectedRow.id).then(res => {
      if (res.success && res.data) {
        return res.data
      } else {
        error(t('settlementsInvoices:notifications.fetchObjections.error'))
        return []
      }
    })
    setSettlementRowObjections(mapObjectionsResponseToTableModel(response))
    setIsLoadingObjections(false)
  }

  const onSaveObjection = async (row: ObjectionTableModel, settlementID: number) => {
    let refreshData: boolean = false
    let objRequest = mapObjectionTableModelToObjectionModel(row)
    objRequest.settlementID = settlementID
    await createObjection(objRequest)
      .then(response => {
        if (response.success) {
          refreshData = true
          info(t('settlementsInvoices:fetchResponse.saveObjection.success'))
        } else {
          error(t('settlementsInvoices:fetchResponse.saveObjection.error'))
        }
      })
      .finally(() => {
        handleObjectionsDialogClose(refreshData, selectedObjection?.objCode)
      })
  }

  const handleObjectionsDialogClose = (refreshData: boolean = false, selectedObjCode?: string) => {
    setShowObjectionsDialog(false)
    setSettlementRowObjections(undefined)
    setSelectedObjection(undefined)
    selectedObjCode && setOpenSettlementsTable(false)
    if (refreshData) {
      refreshTableByContext()
    }
  }

  const handleTerminateObjection = async (
    row: ObjectionTableModel,
    closeObjectionsDialog: boolean = false
  ) => {
    // terminateObjections is shared between ObjectionsCard and ObjectionsDialog
    // in the latter we need to close the dialog after terminating the objection
    getConfirmation(ConfirmationDialogs.TERMINATE_OBJECTION).then(async () => {
      await patchTerminateObjection(row).then(() => {
        if (closeObjectionsDialog) {
          let refreshData: boolean = true
          handleObjectionsDialogClose(refreshData, selectedObjection?.objCode)
        } else {
          // Refresh from card - determine which type of objections to refresh
          if (openSettlementBatchObjections) {
            fetchSettlementBatchsObjections()
          } else if (openSettlementsObjections) {
            fetchSettlementsObjections()
          }
        }
      })
      closeConfirmation()
    })
  }

  //#endregion ------------------- Objections Handlers -----------------------------//

  // #endregion ------------------- Footer Action Buttons Handlers -----------------------------//
  const handleViewListClick = async (selectedRow: SettlementsObjectionsTableModel) => {
    if (selectedRow.objCount > 1000) {
      await getInformation(InformationDialogs.LIMIT_QUERY_RESULTS).then(res => {
        closeConfirmation()
      })
      return
    }
    setOpenSettlementsTable(true)

    if (!invoiceCycleType || !subjectType || !billingType) {
      if (!props.location?.state.invoiceCycleId) {
        return
      }
      const invoiceCycleSuccess = await fetchInvoiceCycleInfo(props.location.state.invoiceCycleId)
      if (!invoiceCycleSuccess) {
        return
      }
    }

    fetchSettlementsTableData(selectedRow.objCode)
    setSelectedObjection(selectedRow)
  }

  const updateExecutionCycleStateHandler = (status: number) => {
    setIsFinishedExecutionCyclePage(status === EnumEstadoCiclo.Finalizado)
  }

  // #endregion Handlers Ends

  // #region Functions Starts
  // #endregion Functions Ends

  // #region Form Definition Starts
  // #endregion Form Definition Ends

  // #region DataTable Config Starts
  // #endregion DataTable Config Ends

  // #region FooterActions Config Starts
  const footerButtons: CardActionProps[] = useMemo(() => {
    const buttons: CardActionProps[] = [
      {
        label: t('settlements:components.kpiCard.button.viewSearchCard'),
        handleActionClick: handleViewSearchCard,
        color: 'primary'
      },
      {
        label: t('settlements:components.kpiCard.button.objectionsHistory'),
        handleActionClick: handleViewObjectionsHistory,
        color: 'primary'
      }
    ]

    // Ejecución cerrada: solo lectura, sin acciones masivas de facturación
    if (isExecutionClosed) {
      return buttons
    }

    const splitButtonOptions: any[] = []

    // Si hay selección, agregar opción personalizada PRIMERO
    if (selectedSettlements.length > 0) {
      splitButtonOptions.push({
        label: t('settlements:components.kpiCard.button.invoiceSelected', {
          count: selectedSettlements.length
        }),
        name: 'invoiceCustomSelection',
        id: EnumSettlementBatchMode.INVOICE_CUSTOM_SELECTION
      })
    }

    // Opciones estándar
    splitButtonOptions.push(
      {
        label: t('settlements:components.kpiCard.button.invoiceAll'),
        name: 'invoiceAll',
        id: EnumSettlementBatchMode.INVOICE_ALL
      },
      {
        label: t('settlements:components.kpiCard.button.invoiceWithoutObjections'),
        name: 'invoiceWithoutObjections',
        id: EnumSettlementBatchMode.INVOICE_WITHOUT_OBJECTIONS
      }
    )

    buttons.push({
      splitButton: {
        options: splitButtonOptions,
        onClick: handleInvoicesSplitButtonClick
      }
    })

    return buttons
  }, [
    isExecutionClosed,
    selectedSettlements,
    t,
    handleViewSearchCard,
    handleViewObjectionsHistory,
    handleInvoicesSplitButtonClick
  ])
  // #endregion FooterActions Config Ends

  // #region Hooks Starts

  const fetchSettlementsStatusKpis = useCallback(async () => {
    setIsFetchingStatusKpis(true)
    await getSettlementsStatusKpis(executionCycleId)
      .then(res => {
        if (res.success && res.data) {
          setStatusKpis(res.data)
        }
      })
      .finally(() => setIsFetchingStatusKpis(false))
  }, [executionCycleId])

  const fetchSettlementAnnouncements = useCallback(async () => {
    fetchSettlementsStatusKpis()
    setIsFetchingKpis(true)
    await getSettlementLandingKpis(executionCycleId)
      .then(res => {
        if (res.success && res.data) {
          setLandingKpis(res.data)
        }
      })
      .finally(() => setIsFetchingKpis(false))
  }, [executionCycleId, fetchSettlementsStatusKpis])

  useEffect(() => {
    invoiceCycleId && fetchInvoiceCycleInfo(invoiceCycleId)
    fetchSettlementAnnouncements()
    if (executionCycleId) {
      setIsFetchingExecution(true)
      fetchInvoiceCycleExecution(executionCycleId)
        .then(res => {
          if (res.success && res.data) {
            setExecutionData(res.data)
          }
        })
        .finally(() => setIsFetchingExecution(false))
    }
  }, [fetchSettlementAnnouncements])

  const fetchInvoiceCycleInfo = async (id: number): Promise<boolean> => {
    setIsFetchingInvoiceCycle(true)
    return await getInvoiceCycleByID(id).then(res => {
      if (res.success && res.data) {
        const invoiceCycle = res.data
        setInvoiceCycleType(invoiceCycle.invoiceCycleType)
        setSubjectType(invoiceCycle.subjectType)
        setBillingType(invoiceCycle.invoiceType)
        setInvoiceCycleDescription(invoiceCycle.description)
        setIsFetchingInvoiceCycle(false)
        return true
      }
      setIsFetchingInvoiceCycle(false)
      return false
      // Handle error
    })
  }

  const fetchSettlementBatchsObjections = async () => {
    setIsFetchingObjections(true)
    await fetchGlobalInvoiceCycleObjections(executionCycleId)
      .then(res => {
        if (res?.success && res?.data) {
          setSettlementBatchObjections(mapGlobalObjectionsResponseToTableModel(res.data))
        }
      })
      .finally(() => setIsFetchingObjections(false))
  }

  const patchTerminateObjection = async (row: ObjectionTableModel) => {
    await terminateObjection(row.id!).then(response => {
      if (response.success) {
        info(t('settlementsInvoices:fetchResponse.terminateObjection.success'))
      } else {
        error(t('settlementsInvoices:fetchResponse.terminateObjection.error'))
      }
    })
  }

  // Limpiar selección cuando cambia el modo
  useEffect(() => {
    if (!customSelectedRowsMode && !viewObjectionsMode) {
      setSelectedSettlements([])
      setSelectedSettlementsData([])
    }
  }, [customSelectedRowsMode, viewObjectionsMode])

  // #endregion Hooks Ends

  const handleReloadExecution = async () => {
    setIsFetchingExecution(true)
    await fetchInvoiceCycleExecution(executionCycleId)
      .then(res => {
        if (res.success && res.data) {
          setExecutionData(res.data)
        }
      })
      .finally(() => setIsFetchingExecution(false))
    refreshTableByContext()
  }

  const {
    notification: executionStatusNotification,
    startWaitingForProcessing,
    isProcessingCase,
    isPausedCase,
    isFinishedCase,
    handleResume,
    handleViewProgress,
    handleRefresh
  } =
    useExecutionStatusNotification({
      executionCycleId,
      status: cycleStatus,
      initialOriginState: props.location?.state?.waitForProcessingFrom,
      processInfo: { invoiceCycleId, cycleName: invoiceCycleDescription, billingType },
      onStatusChange: newStatus =>
        setExecutionData(prevData => (prevData ? { ...prevData, status: newStatus } : prevData)),
      onRefresh: handleReloadExecution,
      onViewProgress: () =>
        history.push(NonAuthRoutes.invoiceCycleProcessing, {
          executionCycleId,
          invoiceCycleId,
          cycleName: invoiceCycleDescription,
          billingType,
          originState: cycleStatus
        })
    })

  return (
    <Page
      layout={PageLayout.SINGLE_COLUMN}
      fullWidth={true}
      notification={executionStatusNotification}
    >
      <PageCenterColumn>
        <PageHeader
          title={
            isExecutionClosed
              ? t('settlements:header.titleFinished')
              : t('settlements:header.title')
          }
        >
          <Button
            variant="text"
            color="primary"
            onClick={() => history.push(NonAuthRoutes.invoiceCyclesNew)}
          >
            {t('settlements:header.buttons.goBack')}
          </Button>
          {!isExecutionClosed && (
            <Button variant="text" color="primary" onClick={openDialog}>
              {t('settlements:header.buttons.closeExecutionCycle')}
            </Button>
          )}
        </PageHeader>
        <SettlementKpiBlocksCard
          title={invoiceCycleDescription}
          invoiceCycleType={invoiceCycleType}
          executionData={executionData}
          isFetchingExecution={isFetchingExecution}
          getExecutionState={updateExecutionCycleStateHandler}
          footerActions={footerButtons}
          statusKpis={statusKpis}
          isLoadingStatusKpis={isFetchingStatusKpis}
        >
          <SettlementKpiBlocks
            statusKpis={statusKpis}
            isLoadingStatusKpis={isFetchingStatusKpis}
            kpis={landingKpis}
            isLoadingKpis={isFetchingKpis}
            activeKpi={activeKpi}
            onKpiClick={handleKpiClick}
            onRegenerateClick={isExecutionClosed ? undefined : handleRegenerateExecutionCycle}
            onViewDetailClick={handleViewSettlementsInvoices}
            isProcessing={isProcessingCase}
            isPaused={isPausedCase}
            isFinished={isFinishedCase}
            onCheckAgainClick={handleResume}
            onViewProgressClick={handleViewProgress}
            onRefreshClick={handleRefresh}
          />
        </SettlementKpiBlocksCard>
        <Grid
          container
          spacing={2}
          ref={resultsRef}
          style={{ marginTop: '8px', scrollMarginTop: '88px' }}
        >
          <Grid item sm={12} md={5}>
            <SettlementObjectionsCard
              show={openSettlementsObjections}
              data={settlementObjections}
              isLoading={isFetchingObjections}
              onViewListClick={handleViewListClick}
            />
            <SearchFilterCard
              showCard={showSearchFilter}
              filters={searchFilters}
              cycleState={EnumEstadoCiclo.PdteValidarListado}
              onSearch={handleSearchFilterSubmit}
              onCancel={handleCancelSearch}
            />
          </Grid>
          <Grid item sm={12} md={openSettlementsObjections || showSearchFilter ? 7 : 12}>
            <GlobalObjectionsCard
              title={t('settlements:components.batchObjectionsCard.title')}
              className={classes.card}
              raised={false}
              show={openSettlementBatchObjections}
              isLoading={isFetchingObjections}
              objectionsData={settlementBatchObjections}
              onTerminateObjection={row => handleTerminateObjection(row)}
              hasEditPermission={hasPermission(Resources.InvoiceCycles, [UserPermission.EDIT])}
            />
            <SettlementsTableCard
              key={settlementsTableKey}
              isSettlements={true}
              show={openSettlementsTable}
              data={settlementsTableData}
              isLoading={isFetchingSettlementsData || isFetchingInvoiceCycle}
              invoiceCycleType={invoiceCycleType}
              subjectType={subjectType}
              billingType={billingType}
              cycleIsFinished={isExecutionClosed}
              onTerminateObjectionsClick={handleTerminateObjectionsClick}
              onViewObjectionsClick={handleViewSettlementObjections}
              onSelectionChange={handleSettlementsSelectionChange}
              customToolbarActionsConfig={
                customSelectedRowsMode
                  ? EnumSettlementTableCustomActionConfig.CUSTOM_SELECTION
                  : viewObjectionsMode
                    ? EnumSettlementTableCustomActionConfig.VIEW_OBJECTIONS
                    : undefined
              }
              onExitButtonClick={handleRestartModesButtonClick}
            />
          </Grid>
        </Grid>

        <ObjectionsDialog
          open={showObjectionsDialog}
          handleClose={() => handleObjectionsDialogClose(false)}
          dataTableTitle={t('settlementsInvoices:dialogs.invoicesObjections.title')}
          objectionsData={isLoadingObjections ? undefined : settlementRowObjections}
          tableUserPermissions={getUserPermissions(Pages.Settlements)}
          hasEditPermission={hasPermission(Resources.InvoiceCycles, [UserPermission.EDIT])}
          allowInsert
          onRowAdd={row => onSaveObjection(row, settlementRow?.id!)}
          onTerminateObjection={row => handleTerminateObjection(row, true)}
          showDescription
          taskDomain={EnumDominioTarea.Liquidacion}
          canBulkTerminate
          canModifyCriticality
          onRefresh={handleRefreshObjectionsDialog}
        />

        <TerminateObjectionsDialog
          open={showTerminateObjectionsDialog}
          handleClose={() => setShowTerminateObjectionsDialog(false)}
          selectedRows={terminateObjectionsRows}
          domain={EnumDominioTarea.Liquidacion}
          entityIdField="id"
          defaultObjMasterId={selectedObjection?.objMasterId}
          onSuccess={handleRefreshSettlementsAfterTerminate}
        />

        <CloseExecutionCycleDialog
          open={isOpen}
          onClose={closeDialog}
          hasOpenSettlements={openSettlements.length > 0}
          cycleName={invoiceCycleDescription}
          onCloseWithoutInvoiceGeneration={onCloseWithoutInvoices}
          onCloseWithInvoiceGeneration={onCloseWithInvoices}
          executionCycleId={executionCycleId}
          invoiceCycleId={invoiceCycleId!}
          invoiceCycleType={invoiceCycleType}
          subjectType={subjectType}
          billingType={billingType}
        />

        <RegenerateExecutionCycleDialog
          open={regenerateDialogOpen}
          onClose={handleRegenerateDialogClose}
          initialData={regenerateInitialData}
        />
      </PageCenterColumn>
    </Page>
  )
}
